/**
 * AI LEADER ACTION ENGINE LOGIC
 * Genererer skreddersydde tiltak for ledere basert på avdelingsspesifikke data.
 */

export const generateLeaderActionPlan = (input) => {
  const { 
    leaderName, deptName, weScore, stress, cri, psi, 
    leadership, roleClarity, comms, comments 
  } = input;

  const priorities = [];
  const actions = [];
  const teamActions = [];
  
  // 1. Leadership Score Logic
  if (leadership < 55) {
    priorities.push("Styrke leders tilstedeværelse og tydelighet");
    actions.push({
      title: "Etablere 'Open Door' timer",
      desc: "Sett av 2 faste timer i uken hvor du er tilgjengelig for uformelle samtaler uten agenda."
    });
    actions.push({
      title: "Strukturert 1-til-1 oppfølging",
      desc: "Gjennomfør ukentlige 15-minutters sjekk-inn med fokus på støttebehov."
    });
  }

  // 2. PSI Logic
  if (psi < 50) {
    priorities.push("Bygge psykologisk trygghet og ytringskultur");
    teamActions.push({
      title: "Møte-etikette: 'Runden rundt bordet'",
      desc: "Sikre at alle stemmer blir hørt før beslutninger fattes i teammøter."
    });
    actions.push({
      title: "Sårbarhets-modellering",
      desc: "Del en nylig læringshistorie eller en feil du gjorde for å normalisere dette i teamet."
    });
  }

  // 3. Stress Logic
  if (stress > 70) {
    priorities.push("Håndtere arbeidsbelastning og forebygge utbrenthet");
    actions.push({
      title: "Prioriterings-vask",
      desc: "Gå gjennom teamets 'To-Do' liste og fjern eller utsett 20% av lav-verdi oppgaver."
    });
    teamActions.push({
      title: "Restitusjons-regler",
      desc: "Etablere felles kjøreregler for kommunikasjon etter arbeidstid og i helger."
    });
  }

  // 4. Conflict logic
  if (cri > 60) {
    priorities.push("De-eskalering av relasjonelle gnisninger");
    actions.push({
      title: "Konflikt-kartlegging",
      desc: "Book samtaler med nøkkelpersoner for å forstå de uformelle mønstrene som skaper friksjon."
    });
    teamActions.push({
      title: "Communication Reset Workshop",
      desc: "Fasilitert sesjon for å definere nye spilleregler for profesjonell uenighet."
    });
  }

  // 5. Role Clarity Logic
  if (roleClarity < 55) {
    priorities.push("Avklare ansvarsområder og forventninger");
    actions.push({
      title: "RASCI-gjennomgang",
      desc: "Oppdater ansvarsmatrisen for teamets 3 viktigste prosesser."
    });
  }

  return {
    meta: {
      leader: leaderName,
      dept: deptName,
      summary: `Som leder for ${deptName} står du i en situasjon hvor ${stress > 70 ? 'høyt arbeidspress' : 'relasjonell usikkerhet'} krever din aktive oppmerksomhet. Teamet opplever ${leadership < 55 ? 'behov for tettere støtte' : 'god støtte, men utfordres av rammefaktorer'}.`
    },
    priorities: priorities.slice(0, 3),
    timeline: {
      day30: actions.slice(0, 2),
      day60: teamActions.slice(0, 2),
      day90: [
        { title: "Evaluering av effekt", desc: "Gjennomfør ny pulsmåling for å verifisere endring." },
        { title: "Standardisering", desc: "Integrer de mest vellykkede tiltakene i teamets faste årshjul." }
      ]
    },
    communication: {
      points: [
        "Anerkjenn de faktiske resultatene fra kartleggingen åpent.",
        "Kommuniser at du tar stress-nivået på alvor og vil beskytte teamets kapasitet.",
        "Inviter til medvirkning i utformingen av de nye rutinene."
      ],
      script: `Jeg har sett på resultatene våre, og ser at vi har en utfordring med ${stress > 70 ? 'arbeidspress' : 'trygghet'}. Mitt mål er at vi sammen skal finne en bedre balanse, og jeg vil begynne med å...`
    },
    cadence: "Ukentlig 1-til-1 (15 min) + Månedlig kultur-refleksjon (45 min)",
    checklist: [
      "Er 1-til-1 samtaler booket i kalenderen?",
      "Har jeg delt min egen læringshistorie denne måneden?",
      "Er ansvarsmatrisen oppdatert og delt?",
      "Har vi feiret en 'god feil' i teamet?"
    ]
  };
};

export const mockLeaderInput = {
  leaderName: "Sarah Chen",
  deptName: "Utviklingsavdelingen",
  weScore: 42,
  stress: 82,
  cri: 25,
  psi: 38,
  leadership: 45,
  roleClarity: 52,
  comms: 48,
  comments: ["Vi har for mye å gjøre", "Det føles ikke trygt å si ifra om forsinkelser", "Leder er sjelden tilgjengelig for sparring"]
};